import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Truck, Shield, MessageCircle, Star, Zap, Flame, Clock, ShoppingCart, Search, Check } from "lucide-react";
import api, { formatMoney } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { fireAddToCartConversion } from "@/lib/gtag";
import { toast } from "sonner";

function getSessionId() {
  let sid = sessionStorage.getItem("cc_session_id");
  if (!sid) { sid = `s_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`; sessionStorage.setItem("cc_session_id", sid); }
  return sid;
}
function getUTMs() {
  try {
    const url = new URL(window.location.href);
    ["source", "medium", "campaign", "term", "content"].forEach((k) => {
      const v = url.searchParams.get(`utm_${k}`);
      if (v) sessionStorage.setItem(`utm_${k}`, v);
    });
    const g = (k) => url.searchParams.get(`utm_${k}`) || sessionStorage.getItem(`utm_${k}`) || "";
    return { utm_source: g("source"), utm_medium: g("medium"), utm_campaign: g("campaign"), utm_term: g("term"), utm_content: g("content") };
  } catch { return {}; }
}
export async function trackEvent(tipo, extra = {}) {
  try {
    await api.post("/public/track", { tipo, session_id: getSessionId(), path: window.location.pathname, referrer: document.referrer || "", ...getUTMs(), ...extra });
  } catch (e) { /* silent */ }
}

function useOfferEnd(product) {
  return useMemo(() => {
    if (!product) return null;
    if (product.oferta_hasta) {
      const d = new Date(product.oferta_hasta);
      if (!isNaN(d.getTime()) && d.getTime() > Date.now()) return d;
    }
    const id = product.id || "";
    let hash = 0;
    for (let i = 0; i < id.length; i++) hash = ((hash << 5) - hash) + id.charCodeAt(i);
    const hours = 24 + (Math.abs(hash) % 49);
    const anchor = new Date(); anchor.setUTCHours(0, 0, 0, 0);
    return new Date(anchor.getTime() + hours * 3600 * 1000);
  }, [product]);
}

function useNow(ms = 1000) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), ms); return () => clearInterval(t); }, [ms]);
  return now;
}
function fmt(end, now) { const d = Math.max(0, end - now); return { h: Math.floor(d/3600000), m: Math.floor((d%3600000)/60000), s: Math.floor((d%60000)/1000) }; }

// Normaliza una opción de variante: puede venir como string o como {nombre, precio_extra}
function optLabel(o) {
  if (o == null) return "";
  if (typeof o === "string") return o;
  if (typeof o === "object") return String(o.nombre || o.label || o.value || "");
  return String(o);
}
function optList(arr) { return (arr || []).map(optLabel).filter(Boolean); }

export default function LandingPublic() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [config, setConfig] = useState({ hero_slogan: "Nuestra oferta principal", whatsapp: "50243036012" });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");

  useEffect(() => {
    trackEvent("pageview");
    (async () => {
      setLoading(true);
      try {
        const [pRes, cRes, cfgRes] = await Promise.all([
          api.get("/public/products"),
          api.get("/public/categories"),
          api.get("/public/landing-config"),
        ]);
        setProducts(pRes.data || []);
        setCategories(cRes.data || []);
        setConfig(cfgRes.data || {});
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    })();
  }, []);

  const hero = useMemo(() => products.find((p) => p.es_destacado) || products[0] || null, [products]);
  const rest = useMemo(() => products.filter((p) => p.id !== hero?.id), [products, hero]);
  const filtered = useMemo(() => {
    let list = rest;
    if (cat) list = list.filter((p) => (p.categoria || "") === cat);
    const term = q.trim().toLowerCase();
    if (term) list = list.filter((p) => (p.nombre || "").toLowerCase().includes(term));
    return list;
  }, [rest, q, cat]);

  return (
    <div className="bg-white">
      {hero && <HeroOferta product={hero} config={config} />}

      <section className="bg-black border-y-4 border-[#FFD100]">
        <div className="max-w-7xl mx-auto px-4 py-4 grid grid-cols-2 md:grid-cols-4 gap-3">
          <Beneficio icon={<Truck />} title="Envíos GT" text="A todo el país" />
          <Beneficio icon={<Shield />} title="Garantía" text="Calidad garantizada" />
          <Beneficio icon={<MessageCircle />} title="WhatsApp" text="Pedidos fáciles" />
          <Beneficio icon={<Flame />} title="Ofertas" text="Precios bajos" />
        </div>
      </section>

      <section className="bg-white border-b border-zinc-200 sticky top-14 md:top-16 z-30">
        <div className="max-w-7xl mx-auto px-4 py-3 flex flex-col md:flex-row gap-3 items-stretch md:items-center">
          <div className="relative flex-1 max-w-md">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <input type="text" placeholder="Buscar producto…" value={q} onChange={(e) => setQ(e.target.value)} className="w-full h-10 pl-10 pr-4 border border-zinc-300 rounded-md bg-white text-sm font-medium focus:outline-none focus:border-[#16A34A]" data-testid="search-input" />
          </div>
          <div className="flex items-center gap-2 overflow-x-auto flex-1">
            <button onClick={() => setCat("")} className={`whitespace-nowrap px-3 h-10 font-black uppercase text-[11px] tracking-widest border rounded-md transition-colors ${!cat ? "bg-black text-white border-black" : "bg-white text-black border-zinc-300 hover:border-black"}`} data-testid="cat-filter-all">Todas</button>
            {categories.map((c) => (
              <button key={c} onClick={() => setCat(c)} className={`whitespace-nowrap px-3 h-10 font-black uppercase text-[11px] tracking-widest border rounded-md transition-colors ${cat === c ? "bg-black text-white border-black" : "bg-white text-black border-zinc-300 hover:border-black"}`} data-testid={`cat-filter-${c}`}>{c}</button>
            ))}
          </div>
        </div>
      </section>

      <section className="py-8 md:py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-end justify-between mb-6 flex-wrap gap-2">
            <div>
              <h2 className="text-2xl md:text-4xl font-black uppercase tracking-tight flex items-center gap-3"><Flame className="w-8 h-8 text-[#E30613]" /> Ofertas del día</h2>
              <div className="h-1 w-24 bg-[#E30613] mt-2" />
            </div>
            <div className="text-sm font-bold text-zinc-500">{filtered.length} productos disponibles</div>
          </div>
          {loading ? (
            <div className="text-center py-16 text-zinc-500">Cargando productos…</div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-16 bg-zinc-50 border-2 border-dashed border-zinc-300">
              <p className="text-zinc-500 font-medium">{rest.length === 0 ? "No hay productos publicados aún. Ve a Panel → Tienda Online para publicarlos." : "No hay productos que coincidan."}</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5">
              {filtered.map((p) => <OfertaCard key={p.id} p={p} />)}
            </div>
          )}
        </div>
      </section>

      <section className="bg-white py-10 md:py-14 border-t border-zinc-200">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-2xl md:text-4xl font-black uppercase tracking-tight text-black">¿Dudas o pedidos especiales?</h2>
          <p className="text-base md:text-lg text-zinc-600 mt-3">Escríbenos por WhatsApp y te atendemos al instante.</p>
          <a href={`https://wa.me/${config.whatsapp || "50243036012"}`} target="_blank" rel="noreferrer" onClick={() => trackEvent("whatsapp_click", { extra: { origen: "cta_final" } })} className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#128C7E] text-white font-black px-8 py-4 mt-6 uppercase tracking-widest text-sm shadow-lg rounded-md">
            <MessageCircle className="w-5 h-5" /> Escribir por WhatsApp
          </a>
        </div>
      </section>
    </div>
  );
}

// Mapa de nombre de color en español a hex para mostrar el tono real en swatches
const COLOR_HEX = {
  blanco: "#FFFFFF", negro: "#111111", gris: "#9CA3AF", "gris oscuro": "#4B5563", "gris claro": "#D1D5DB",
  beige: "#E8DBC6", crema: "#F5EFE0", marfil: "#FFFFF0",
  rojo: "#DC2626", "rojo oscuro": "#7F1D1D", vino: "#7B1E3A", cereza: "#B91C1C",
  azul: "#1D4ED8", "azul rey": "#1E3A8A", "azul marino": "#0B1E4A", "azul cielo": "#38BDF8", turquesa: "#14B8A6",
  celeste: "#7DD3FC",
  verde: "#16A34A", "verde oscuro": "#065F46", olivo: "#4D7C0F", menta: "#A7F3D0",
  amarillo: "#FACC15", mostaza: "#CA8A04", dorado: "#D4AF37",
  naranja: "#F97316", coral: "#FB7185", salmon: "#FCA5A5",
  rosa: "#F472B6", "rosa pastel": "#F9C6D9", palo: "#E4B7B7", fucsia: "#D946EF",
  morado: "#7C3AED", violeta: "#8B5CF6", lila: "#C4B5FD", lavanda: "#DDD6FE",
  cafe: "#8B5A2B", café: "#8B5A2B", chocolate: "#5D3A1A", canela: "#B7793A", "cafe claro": "#B08968",
  madera: "#A47148", tostado: "#C9A382",
  plateado: "#C0C0C0", plata: "#C0C0C0",
};
function colorHex(name) {
  if (!name) return "#E5E7EB";
  const k = String(name).trim().toLowerCase();
  if (COLOR_HEX[k]) return COLOR_HEX[k];
  // fallback: buscar palabra clave (ej "rojo claro" → rojo)
  for (const key of Object.keys(COLOR_HEX)) {
    if (k.includes(key)) return COLOR_HEX[key];
  }
  return "#E5E7EB";
}

// Selector visual de colores: swatch de tono real + nombre debajo, con check al seleccionar
function ColorSwatches({ options, value, onChange, testidPrefix }) {
  const list = optList(options);
  if (list.length === 0) return null;
  return (
    <div className="mt-4">
      <div className="text-[10px] uppercase tracking-widest font-black text-zinc-500 mb-2">Color</div>
      <div className="flex flex-wrap gap-3 justify-center md:justify-start">
        {list.map((c) => {
          const active = value === c;
          const hex = colorHex(c);
          const isWhite = hex.toUpperCase() === "#FFFFFF";
          return (
            <button
              key={c}
              type="button"
              onClick={() => onChange(c)}
              className="flex flex-col items-center gap-1 group"
              data-testid={`${testidPrefix}-${c}`}
            >
              <span
                className={`relative w-10 h-10 rounded-full transition-all ${active ? "ring-2 ring-offset-2 ring-black" : "ring-1 ring-black/10 group-hover:ring-black/40"} ${isWhite ? "border border-zinc-300" : ""}`}
                style={{ backgroundColor: hex }}
              >
                {active && (
                  <svg viewBox="0 0 24 24" className={`absolute inset-0 m-auto w-5 h-5 ${isWhite || ["#FFFFF0","#F5EFE0","#DDD6FE","#A7F3D0","#FCA5A5","#7DD3FC","#F9C6D9"].includes(hex.toUpperCase()) ? "text-black" : "text-white"}`} fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                )}
              </span>
              <span className={`text-[10px] uppercase font-bold tracking-tight ${active ? "text-black" : "text-zinc-600"}`}>{c}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function HeroOferta({ product, config }) {
  const { add } = useCart();
  const end = useOfferEnd(product);
  const now = useNow(1000);
  const { h, m, s } = fmt(end?.getTime() || 0, now);
  const [variant, setVariant] = useState({
    color: optList(product.opciones_color)[0] || "",
    tela: optList(product.opciones_tela)[0] || "",
    tamano: optList(product.opciones_tamano)[0] || "",
    estilo: optList(product.opciones_estilo)[0] || "",
  });
  const hasOferta = product.precio_oferta && product.precio_oferta > 0 && product.precio_oferta < product.precio;
  const precioActual = hasOferta ? product.precio_oferta : product.precio;
  const precioTachado = hasOferta ? product.precio : null;
  const descuento = precioTachado && precioTachado > precioActual ? Math.round((1 - precioActual / precioTachado) * 100) : 0;

  const handleAdd = () => {
    add(product, variant, 1);
    trackEvent("add_to_cart", { producto_id: product.id, monto: precioActual });
    // Google Ads: conversión "Inclusión en el carrito" — tras confirmar que se agregó.
    fireAddToCartConversion();
    toast.success(`${product.nombre} agregado`);
  };

  return (
    <section className="bg-white">
      <div className="max-w-7xl mx-auto px-4 py-6 md:py-14">
        {/* Título arriba en móvil, oculto en desktop (desktop lo tiene a la derecha) */}
        <h1 className="md:hidden text-2xl font-black text-black leading-tight tracking-tight uppercase text-center px-2 mb-4" translate="no">{product.nombre}</h1>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-10 items-center">
          {/* Foto */}
          <div className="w-full">
            <div className="bg-white rounded-lg overflow-hidden shadow-md aspect-square">
              {product.imagen ? (
                <img src={product.imagen} alt={product.nombre} className="w-full h-full object-cover" translate="no" />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-zinc-50 to-zinc-100">
                  <span className="text-zinc-200 text-8xl font-black">CC</span>
                </div>
              )}
            </div>
          </div>

          {/* Panel derecho: información y CTA */}
          <div className="flex flex-col gap-4">
            {/* Título en desktop */}
            <h1 className="hidden md:block text-3xl lg:text-5xl font-black text-black leading-tight tracking-tight uppercase" data-testid="hero-title" translate="no">{product.nombre}</h1>
            {product.descripcion && <p className="hidden md:block text-base text-zinc-600" translate="no">{product.descripcion}</p>}

            {/* Precios */}
            <div className="flex items-baseline gap-3 flex-wrap justify-center md:justify-start">
              {precioTachado && precioTachado > precioActual && (
                <div className="flex flex-col items-center md:items-start">
                  <span className="text-[9px] uppercase tracking-widest text-zinc-400 font-black">Antes</span>
                  <span className="text-lg md:text-xl font-bold text-zinc-400 line-through -mt-1">{formatMoney(precioTachado)}</span>
                </div>
              )}
              <span className="text-4xl md:text-5xl font-black text-[#E30613]">{formatMoney(precioActual)}</span>
              {descuento > 0 && <span className="bg-[#E30613] text-white text-xs font-black px-2 py-1 uppercase rounded">-{descuento}%</span>}
            </div>

            {/* Cronómetro */}
            {end && (
              <div className="bg-black text-white p-3 md:p-4 rounded-lg" data-testid="hero-countdown">
                <div className="text-[10px] uppercase tracking-widest text-[#FFB800] font-black flex items-center justify-center md:justify-start gap-1 mb-2"><Clock className="w-3 h-3" /> Termina en:</div>
                <div className="flex gap-2 md:gap-3 font-mono justify-center md:justify-start">
                  <TimeBox label="Horas" value={h} />
                  <span className="text-3xl md:text-4xl font-black self-center">:</span>
                  <TimeBox label="Min" value={m} />
                  <span className="text-3xl md:text-4xl font-black self-center">:</span>
                  <TimeBox label="Seg" value={s} />
                </div>
              </div>
            )}

            {/* Selector de colores dinámico con swatch real */}
            <ColorSwatches options={product.opciones_color} value={variant.color} onChange={(c) => setVariant({ ...variant, color: c })} testidPrefix="hero-color" />

            {/* CTA verde principal + micro-copy */}
            <div className="flex flex-col items-stretch gap-1 mt-2">
              <Link
                to="/carrito"
                onClick={handleAdd}
                data-testid="hero-buy-btn"
                className="w-full bg-[#16A34A] hover:bg-[#15803D] text-white font-black py-4 uppercase tracking-widest text-base md:text-lg text-center rounded-md shadow-md transition-colors"
              >
                !! COMPRA YA !!
              </Link>
              <div className="text-center text-sm md:text-base font-bold text-[#16A34A]">¡Pagas al recibir!</div>
            </div>

            {/* Botón secundario Agregar al carrito */}
            <button
              onClick={handleAdd}
              data-testid="hero-add-btn"
              className="w-full bg-white hover:bg-zinc-50 text-black font-black py-3 uppercase tracking-widest text-sm rounded-md border-2 border-black transition-colors flex items-center justify-center gap-2"
            >
              <ShoppingCart className="w-5 h-5" /> Agregar al carrito
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

function TimeBox({ label, value }) {
  const v = String(value).padStart(2, "0");
  return (
    <div className="bg-white text-black px-2 md:px-3 py-1.5 rounded flex-1 text-center">
      <div className="text-2xl md:text-4xl font-black leading-none tabular-nums">{v}</div>
      <div className="text-[8px] md:text-[10px] uppercase tracking-widest font-bold text-zinc-500 mt-0.5">{label}</div>
    </div>
  );
}

function OfertaCard({ p }) {
  const { add } = useCart();
  const end = useOfferEnd(p);
  const now = useNow(1000);
  const { h, m, s } = fmt(end?.getTime() || 0, now);
  const [added, setAdded] = useState(false);
  const hasOferta = p.precio_oferta && p.precio_oferta > 0 && p.precio_oferta < p.precio;
  const precioActual = hasOferta ? p.precio_oferta : p.precio;
  const precioTachado = hasOferta ? p.precio : null;
  const handleAdd = (e) => {
    e.preventDefault(); e.stopPropagation();
    add(p, {
      color: optList(p.opciones_color)[0] || "", tela: optList(p.opciones_tela)[0] || "",
      tamano: optList(p.opciones_tamano)[0] || "", estilo: optList(p.opciones_estilo)[0] || "",
    }, 1);
    trackEvent("add_to_cart", { producto_id: p.id, monto: precioActual });
    // Google Ads: conversión "Inclusión en el carrito" — tras confirmar que se agregó.
    fireAddToCartConversion();
    setAdded(true); toast.success(`${p.nombre} agregado`);
    setTimeout(() => setAdded(false), 1500);
  };

  return (
    <Link to={`/producto/${p.id}`} className="group block bg-white border-2 border-zinc-200 hover:border-[#E30613] hover:shadow-xl transition-all overflow-hidden relative" data-testid={`product-card-${p.id}`}>
      <div className="absolute top-2 left-2 z-10 bg-[#E30613] text-white text-[9px] md:text-[10px] font-black px-2 py-1 uppercase tracking-widest flex items-center gap-1"><Flame className="w-3 h-3" /> Oferta</div>
      <div className="aspect-square bg-zinc-100 overflow-hidden">
        {p.imagen ? <img src={p.imagen} alt={p.nombre} className="w-full h-full object-cover group-hover:scale-105 transition-transform" translate="no" /> : <div className="w-full h-full flex items-center justify-center"><span className="text-zinc-300 text-4xl font-black">CC</span></div>}
      </div>
      <div className="p-3">
        <div className="text-[9px] md:text-[10px] uppercase tracking-widest text-zinc-500 font-bold" translate="no">{p.categoria || "Producto"}</div>
        <div className="font-bold text-sm md:text-base line-clamp-2 min-h-[2.5rem] leading-tight mt-0.5" translate="no">{p.nombre}</div>
        <div className="mt-2 flex items-baseline gap-2 flex-wrap">
          <span className="text-lg md:text-xl font-black text-[#E30613]">{formatMoney(precioActual)}</span>
          {precioTachado && precioTachado > precioActual && <span className="text-xs font-bold text-zinc-400 line-through">{formatMoney(precioTachado)}</span>}
        </div>
        <div className="mt-2 bg-black text-white rounded flex items-center justify-center gap-1 py-1.5 font-mono text-xs md:text-sm font-black" data-testid={`countdown-${p.id}`}>
          <Clock className="w-3 h-3 text-[#FFD100]" />
          <span className="tabular-nums">{String(h).padStart(2, "0")}:{String(m).padStart(2, "0")}:{String(s).padStart(2, "0")}</span>
        </div>
        <button onClick={handleAdd} className={`mt-2 w-full font-black text-xs py-2 uppercase tracking-widest border-2 border-black flex items-center justify-center gap-1 transition-colors ${added ? "bg-green-600 text-white border-green-600" : "bg-[#FFD100] text-black hover:bg-black hover:text-[#FFD100]"}`} data-testid={`add-btn-${p.id}`}>
          {added ? <><Check className="w-3 h-3" /> Agregado</> : <><ShoppingCart className="w-3 h-3" /> Agregar</>}
        </button>
      </div>
    </Link>
  );
}

function Beneficio({ icon, title, text }) {
  return (
    <div className="flex items-center gap-2 md:gap-3 text-white">
      <div className="bg-[#FFD100] text-black p-2 md:p-2.5 rounded-full flex-shrink-0">{icon}</div>
      <div className="min-w-0">
        <div className="font-black text-xs md:text-sm uppercase truncate">{title}</div>
        <div className="text-[10px] md:text-xs text-zinc-400 truncate">{text}</div>
      </div>
    </div>
  );
}
